import { createTheme } from "@mui/material/styles";
import { esES as dataGridesES } from "@mui/x-data-grid";
import { esES as coreesES } from "@mui/material/locale";
import { esES } from "@mui/x-date-pickers";
import { TypographyOptions } from "@mui/material/styles/createTypography";
import { letterSpacing } from "@mui/system";

export enum EFontFamilies {
  // GOTHAM = "GothamHTF",
  DISPLAY = "SF Pro Display",
  DEFAULT_FONT = "SF Pro Display",
  SECONDARY_FONT = "Inter"
}

export const Csizes = {
  input: 35,
  input2: 40,
  navBarHeight: 74
};

const typography: TypographyOptions = {
  fontFamily: [
    EFontFamilies.DEFAULT_FONT,
    "-apple-system",
    "BlinkMacSystemFont",
    "sans-serif"
  ].join(","),
  h1: {
    fontSize: "1.5rem", // 24px en mobile
    fontFamily: EFontFamilies.DISPLAY,
    fontWeight: 700,
    lineHeight: 1.2,
    "@media (min-width:600px)": { fontSize: "2rem" }, // 32px en tablet
    "@media (min-width:960px)": { fontSize: "2.5rem" } // 40px en desktop
  },
  h2: {
    fontSize: "1.375rem", // 22px en mobile
    fontFamily: EFontFamilies.DISPLAY,
    fontWeight: 700,
    lineHeight: 1.3,
    "@media (min-width:600px)": { fontSize: "1.75rem" }, // 28px en tablet
    "@media (min-width:960px)": { fontSize: "2rem" } // 32px en desktop
  },
  h3: {
    fontSize: "1.25rem", // 20px en mobile
    fontFamily: EFontFamilies.DISPLAY,
    fontWeight: 700,
    lineHeight: 1.4,
    "@media (min-width:600px)": { fontSize: "1.5rem" }, // 24px en tablet
    "@media (min-width:960px)": { fontSize: "1.75rem" } // 28px en desktop
  },
  h4: {
    fontSize: "1.125rem", // 18px en mobile
    fontFamily: EFontFamilies.DISPLAY,
    fontWeight: 700,
    lineHeight: 1.4,
    "@media (min-width:600px)": { fontSize: "1.25rem" }, // 20px en tablet
    "@media (min-width:960px)": { fontSize: "1.5rem" } // 24px en desktop
  },
  h5: {
    fontSize: "1rem", // 16px en mobile
    fontWeight: 700,
    fontFamily: EFontFamilies.DISPLAY,
    lineHeight: 1.5,
    "@media (min-width:600px)": { fontSize: "1.125rem" }, // 18px en tablet
    "@media (min-width:960px)": { fontSize: "1.25rem" } // 20px en desktop
  },
  h6: {
    fontSize: "0.875rem", // 14px en mobile
    fontWeight: 700,
    fontFamily: EFontFamilies.DISPLAY,
    lineHeight: 1.6,
    "@media (min-width:600px)": { fontSize: "1rem" }, // 16px en tablet
    "@media (min-width:960px)": { fontSize: "1.125rem" } // 18px en desktop
  },
  subtitle1: {
    fontSize: "1rem", // 16px en mobile
    fontFamily: EFontFamilies.DISPLAY,
    fontWeight: 600,
    lineHeight: 1.5,
    "@media (min-width:600px)": { fontSize: "1rem" }, // 16px en tablet
    "@media (min-width:960px)": { fontSize: "1.125rem" } // 18px en desktop
  },
  subtitle2: {
    fontSize: "0.875rem", // 14px en mobile
    fontFamily: EFontFamilies.DISPLAY,
    fontWeight: 600,
    lineHeight: 1.6,
    "@media (min-width:600px)": { fontSize: "1rem" }, // 16px en tablet
    "@media (min-width:960px)": { fontSize: "0.9375rem" } // 15px en desktop
  },
  body1: {
    fontSize: "0.875rem", // 14px en mobile
    fontWeight: 400,
    fontFamily: EFontFamilies.SECONDARY_FONT,
    lineHeight: 1.6,
    "@media (min-width:600px)": { fontSize: "0.975rem" }, // 16px en tablet
    "@media (min-width:960px)": { fontSize: "1.125rem" } // 18px en desktop
  },
  body2: {
    fontSize: "0.75rem", // 12px en mobile
    fontWeight: 400,
    fontFamily: EFontFamilies.SECONDARY_FONT,
    lineHeight: 1.6,
    "@media (min-width:600px)": { fontSize: "0.875rem" }, // 14px en tablet
    "@media (min-width:960px)": { fontSize: "0.9375rem" } // 15px en desktop
  },
  button: {
    fontSize: "0.75rem", // 14px en mobile
    fontWeight: 600,
    textTransform: "none",
    fontFamily: EFontFamilies.DEFAULT_FONT,
    lineHeight: 1.5,
    letterSpacing: "0.03em",
    "@media (min-width:600px)": { fontSize: "0.75rem" }, // 14px en tablet
    "@media (min-width:960px)": { fontSize: "0.744rem" } // 16px en desktop
  },
  caption: {
    fontSize: "0.75rem", // 12px en mobile
    fontWeight: 400,
    fontFamily: EFontFamilies.SECONDARY_FONT,
    lineHeight: 1.6,
    "@media (min-width:600px)": { fontSize: "0.8125rem" }, // 13px en tablet
    "@media (min-width:960px)": { fontSize: "0.875rem" } // 14px en desktop
  },
  overline: {
    fontSize: "0.625rem", // 10px en mobile
    fontWeight: 400,
    fontFamily: EFontFamilies.SECONDARY_FONT,
    textTransform: "none",
    letterSpacing: "0.05em",
    lineHeight: 1.6,
    "@media (min-width:600px)": { fontSize: "0.6875rem" }, // 11px en tablet
    "@media (min-width:960px)": { fontSize: "0.75rem" } // 12px en desktop
  }
};

const greyScale = {
  50: "#fafafa",
  100: "#f5f5f5",
  200: "#eeeeee",
  300: "#e0e0e0",
  400: "#bdbdbd",
  500: "#9e9e9e",
  600: "#737373",
  700: "#555555",
  800: "#424242",
  900: "#212121"
};

const theme = createTheme(
  {
    shape: {
      borderRadius: 15
    },
    palette: {
      primary: {
        main: "#002e7c", // Color azul principal - mantenido según requerimiento
        light: "#3358a4", // Azul más claro para hover y estados activos
        dark: "#00204f", // Azul más oscuro para estados presionados
        contrastText: "#ffffff"
      },
      secondary: {
        main: "#dc1c2e", // Color rojo principal - mantenido según requerimiento
        light: "#ff5252", // Rojo más claro para hover y estados activos
        dark: "#a30000", // Rojo más oscuro para estados presionados
        contrastText: "#ffffff"
      },
      error: {
        light: "#ff6b6b", // Rojo error más brillante
        main: "#e53935", // Rojo error principal (más accesible)
        dark: "#b71c1c", // Rojo error oscuro
        contrastText: "#ffffff"
      },
      warning: {
        light: "#ffb74d", // Naranja más brillante
        main: "#f9a825", // Naranja principal
        dark: "#e65100", // Naranja oscuro
        contrastText: "#ffffff"
      },
      success: {
        light: "#81c784", // Verde más brillante
        main: "#4caf50", // Verde principal
        dark: "#2e7d32", // Verde oscuro
        contrastText: "#ffffff"
      },
      info: {
        light: "#64b5f6", // Azul info más brillante
        main: "#2196f3", // Azul info principal
        dark: "#0d47a1", // Azul info oscuro
        contrastText: "#ffffff"
      },
      grey: {
        ...greyScale
      },
      background: {
        default: "#ffffff",
        paper: "#ffffff"
      },
      text: {
        primary: "#212121",
        secondary: greyScale[600],
        disabled: greyScale[500]
      },
      divider: greyScale[300],
      action: {
        active: "rgba(0, 0, 0, 0.54)",
        hover: "rgba(0, 0, 0, 0.04)",
        selected: "rgba(0, 46, 124, 0.08)", // Basado en primary.main
        disabled: "rgba(0, 0, 0, 0.26)",
        disabledBackground: "#f1f5f9",
        focus: "rgba(0, 0, 0, 0.12)"
      }
    },

    components: {
      MuiAppBar: {
        defaultProps: {
          style: {
            height: Csizes.navBarHeight
          }
        }
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true // No more ripple, on the whole application 💣!
        }
      },
      // chip
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: EFontFamilies.DEFAULT_FONT,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.5,
            letterSpacing: "0.05em",
            "&.MuiChip-deletable": {
              "&:focus": {
                backgroundColor: greyScale[100]
              }
            }
          },
          deleteIcon: {
            color: greyScale[600],
            "&:hover": {
              color: greyScale[600]
            }
          }
        }
      },

      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            lineHeight: 1.5,
            padding: 0,
            fontFamily: EFontFamilies.DEFAULT_FONT,
            transition: "all 0.2s ease-in-out",
            "&[data-shrink='true']": {
              fontSize: "0.79rem",
              paddingLeft: "0.3125rem",
              transform: "translate(0.875rem, -0.5625rem) scale(0.75)",
              color: "#002e7c",
              zIndex: 1,
              backgroundColor: "white"
            },
            "&[data-shrink='false']": {
              transform: "translate(0.625rem, 0.575rem) scale(1)",
              fontSize: "0.75rem",
              color: "#002e7c"
            }
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            boxSizing: "border-box",
            maxHeight: Csizes.input,
            padding: "4px 6px",
            fontSize: 12,
            fontWeight: 500,
            fontFamily: EFontFamilies.DEFAULT_FONT,
            color: "#616161",
            "&.MuiOutlinedInput-root": {
              borderRadius: 20
            },

            "&.MuiInputBase-adornedStart": {
              marginRight: 3
            },
            "& ::placeholder": {
              color: greyScale[400],
              fontFamily: EFontFamilies.DEFAULT_FONT,
              fontSize: 12,
              letterSpacing: "0.05em"
            },
            input: {
              fontFamily: EFontFamilies.DEFAULT_FONT,
              fontSize: 12,
              letterSpacing: "0.05em",
              color: greyScale[600],
              fontWeight: 500
            }, // disabled:
            "&.Mui-disabled": {
              backgroundColor: "#f1f5f9",
              cursor: "not-allowed"
            }
          }
        }
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontSize: "0.875rem", // 14px
            fontFamily: EFontFamilies.DEFAULT_FONT,
            color: greyScale[700],
            fontWeight: 500
          }
        }
      }
    },
    typography: typography
  },
  esES, // x-date-pickers translations
  dataGridesES, // x-data-grid translations
  coreesES
);

export default theme;
